import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "./api";
import { useAuth } from "./AuthContext";

/**
 * BrandContext exposes both:
 *   - `brand`: static branding (app name, colors, plan features)
 *   - `terms`: the active organization's Industry Profile vocabulary
 *
 * Terms are loaded from the current coach's org via /api/orgs/mine ONLY
 * once AuthProvider has resolved that the user is signed in AND is a coach.
 * For guests / parents / admins we use the default (team_sports) profile so
 * every public page renders identical copy to today.
 *
 * IMPORTANT startup ordering
 * --------------------------
 * BrandProvider MUST be rendered INSIDE AuthProvider so it can gate the
 * `/api/orgs/mine` probe on the auth state. Firing that probe unauthed on
 * first paint (as this component used to do) triggered the global 401
 * handler and hard-redirected guest visitors to /login — the P0 blank-white
 * regression tracked in the July 31 fix.
 *
 * Refresh: after PATCH /api/orgs/mine changes the profile, callers dispatch
 * `window.dispatchEvent(new Event('terms:changed'))`. BrandProvider hears
 * that and re-fetches so the UI re-renders in the new vocabulary without a
 * page reload.
 */

// Fallback profile — exactly matches the current MVP wording so any code
// path that renders before Terms load looks identical to today.
const DEFAULT_TERMS = Object.freeze({
  leader: "Coach", leader_lower: "coach",
  leader_plural: "Coaches", leader_plural_lower: "coaches",
  group: "Team", group_lower: "team",
  group_plural: "Teams", group_plural_lower: "teams",
  participant: "Player", participant_lower: "player",
  participant_plural: "Players", participant_plural_lower: "players",
  guardian: "Parent", guardian_lower: "parent",
  guardian_plural: "Parents",
  roster: "Roster",
  add_participant: "Add player",
  add_group: "Create team",
  empty_group_hint: "Add your first team so you can invite players and create fees.",
  empty_participant_hint: "Add players to this team so they can receive fees.",
  join_group_email_subject: "Join our {group_name} on Squad Fee",
  join_group_share_text: "You've been invited to join our {group_name} on Squad Fee.",
  reminder_email_subject: "Payment reminder for {charge_title}",
  review_tab_label: "To Review",
});

const BrandContext = createContext(null);

export function BrandProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [brand, setBrand] = useState(null);
  const [terms, setTerms] = useState(DEFAULT_TERMS);
  const [profileId, setProfileId] = useState("team_sports");

  // Load /brand catalog once. This endpoint is PUBLIC (no auth) so it's
  // safe to fire regardless of who's visiting.
  useEffect(() => {
    let cancelled = false;
    api
      .get("/brand")
      .then((r) => { if (!cancelled) setBrand(r.data); })
      .catch(() => { if (!cancelled) setBrand({ app_name: "Squad Fee" }); });
    return () => { cancelled = true; };
  }, []);

  // Look up the current org's terms. Only meaningful for coaches; runs
  // once auth has resolved so we don't fire /orgs/mine as a guest.
  const refreshTerms = useCallback(async () => {
    // Skip for guests / non-coach roles — DEFAULT_TERMS is already correct
    // for the public marketing site, parent app, and admin console.
    if (!user || user === false || user?.role !== "coach") {
      setTerms(DEFAULT_TERMS);
      setProfileId("team_sports");
      return;
    }
    try {
      const { data: org } = await api.get("/orgs/mine");
      if (!org?.industry_profile) return;
      setProfileId(org.industry_profile);
      // Prefer the already-loaded brand catalog; only fall back to a
      // fresh fetch if BrandProvider hasn't finished loading it yet.
      let catalog = brand?.industry_profiles;
      if (!catalog) {
        try {
          const { data: b } = await api.get("/brand");
          catalog = b?.industry_profiles;
        } catch {
          catalog = null;
        }
      }
      const profile = (catalog || []).find((p) => p.id === org.industry_profile);
      if (profile?.terms) setTerms(profile.terms);
    } catch {
      // Any 401 here is handled silently by api.js (see SILENT_AUTH_PATHS)
      // so we just keep the defaults.
    }
  }, [user, brand]);

  useEffect(() => {
    // Don't fire until AuthProvider has finished its /auth/me check —
    // otherwise a signed-in coach with a stale token would first probe
    // /orgs/mine as a "guest" and race with the actual auth check.
    if (authLoading) return;
    refreshTerms();
  }, [authLoading, refreshTerms]);

  // Listen for the custom "terms:changed" event fired by Settings.jsx
  // after the coach picks a new industry profile, so we re-render the
  // whole app in the new vocabulary without a hard reload.
  useEffect(() => {
    const onChanged = () => refreshTerms();
    window.addEventListener("terms:changed", onChanged);
    return () => window.removeEventListener("terms:changed", onChanged);
  }, [refreshTerms]);

  const value = useMemo(
    () => ({ brand, terms, profileId, refreshTerms }),
    [brand, terms, profileId, refreshTerms],
  );
  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

/**
 * Legacy shape — kept so existing `useBrand()` callers don't need to
 * update. Returns the brand object with an `app_name` fallback.
 */
export function useBrand() {
  const ctx = useContext(BrandContext);
  return (ctx && ctx.brand) || { app_name: "Squad Fee" };
}

/**
 * useTerms — returns the active organization's vocabulary.
 * Usage:
 *   const { t, resolve } = useTerms();
 *   <h1>{t('group_plural')}</h1>          → "Teams" / "Classes" / etc.
 *   <p>{resolve("Add your first {group_lower}")}</p>  → "Add your first team"
 *
 * The returned `terms` object also includes legacy alias keys
 * (team, Team, teams, Teams, athlete, athletes, add_athlete, etc.) so
 * files migrated from the old `termsFor(user.account_type)` helper only
 * need to swap their import + assignment — everything else keeps working.
 */
export function useTerms() {
  const ctx = useContext(BrandContext);
  const rawTerms = (ctx && ctx.terms) || DEFAULT_TERMS;
  const profileId = (ctx && ctx.profileId) || "team_sports";
  const refreshTerms = (ctx && ctx.refreshTerms) || (() => {});

  // Extend the base terms with legacy alias keys so files migrated from
  // the old `termsFor()` helper don't need every string edited — just
  // their import + assignment line.
  const terms = useMemo(() => {
    const t = rawTerms || DEFAULT_TERMS;
    return {
      ...t,
      // Legacy shape used by old callers — keep these forever for
      // backwards compat.
      team: t.group_lower, teams: t.group_plural_lower,
      Team: t.group,       Teams: t.group_plural,
      athlete: t.participant_lower, athletes: t.participant_plural_lower,
      Athlete: t.participant,       Athletes: t.participant_plural,
      active_athletes_label: `Active ${t.participant_plural_lower}`,
      groups_label: t.group_plural,
      add_athlete: t.add_participant,
      archive_athlete: `Archive ${t.participant_lower}`,
      reactivate_athlete: `Reactivate ${t.participant_lower}`,
      empty_team_hint: t.empty_group_hint,
      pricing_hint: `Manage ${t.group_plural_lower} and their ${t.participant_plural_lower}.`,
    };
  }, [rawTerms]);

  const t = useCallback(
    (key) => terms[key] ?? DEFAULT_TERMS[key] ?? key,
    [terms],
  );
  const resolve = useCallback(
    (template) => {
      if (!template) return template;
      // Simple {key} substitution — no format-spec support, keeps it
      // deterministic and safe against user input.
      return template.replace(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g, (m, key) =>
        terms[key] ?? DEFAULT_TERMS[key] ?? m,
      );
    },
    [terms],
  );

  return { t, resolve, terms, profileId, refreshTerms };
}
